import {
  Input,
  inject,
  OnInit,
  Output,
  OnChanges,
  Component,
  DestroyRef,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { LoggerService } from '@services/logger.service';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';

import {
  Workspace,
  DataObject,
  ObjectDefinition,
} from '../../models/core.models';
import { ChildPopupPaneComponent } from '../../models/form.models';
import { NomObjectClass, NomObjectTypes, SortDirection } from '../../constants';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  setCoreFilteredObjectsSortingDirection,
  setCoreFilteredObjectsSortingField,
} from '../../actions/core.actions';

import { AppState } from 'src/app/reducers';

import {
  tap,
  skip,
  filter,
  Observable,
  combineLatest,
  BehaviorSubject,
  distinctUntilChanged,
  switchMap,
  finalize,
} from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { UserObjectsService } from '../../services/user-objects.service';

/** Selection-counter inputs (relocated from the retired ObjectPickerActionsService). */
export interface CounterState {
  currentSelectedCount: number;
  maxSelectedCount: number;
  objectClassIds: number[];
  objectTypeIds: number[];
}

/**
 * Rows the object list asks the server for. A Global Admin with "View All Company
 * Objects" can match thousands of dimensions; rendering them all froze the picker
 * (WAP-1622). Above the cap the list shows the first rows plus a notice.
 */
export const DATAOBJECT_LIST_RESULTS_CAP = 1000;

@Component({
  selector: 'app-dataobject-list',
  templateUrl: './dataobject-list.component.html',
  styleUrls: ['./dataobject-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataObjectListComponent
  implements OnInit, OnChanges, ChildPopupPaneComponent
{
  @Input() searchString$: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );
  @Input() workspace: Workspace;
  @Input() selectedObjectIds: Map<string, ObjectDefinition> = new Map();
  @Input() objectClassIds: Array<number> = [NomObjectClass.FEED];
  @Input() objectTypeIds: Array<number>;

  @Input() singleSelectionActive = false;
  @Input() showSelectedOnly = false;
  @Input() showSearch = false;
  @Input() maxSelectedCount = 0;

  @Input() formGroupPropertyName: string;

  // * Lazy loading: when both inputs are provided, the component defers
  // * fetching until its tab becomes active for the first time.
  @Input() tabId?: string;
  @Input() activeTabId$?: Observable<string | null>;

  @Output() componentDataChange: EventEmitter<Map<string, ObjectDefinition>> =
    new EventEmitter();
  @Output() formGroupChange = new EventEmitter<FormGroup>();

  searchString = '';
  NomObjectClass = NomObjectClass;

  // * this component's *own* loading state, raised by its own `runFetch`
  objectsLoading$ = new BehaviorSubject<boolean>(false);

  // * track showSelectedOnly changes
  private showSelectedOnly$: BehaviorSubject<boolean>;

  // * Counter state management
  private counterState: CounterState;

  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private userObjects = inject(UserObjectsService);
  private cdr = inject(ChangeDetectorRef);

  formGroup: FormGroup = new FormGroup({});

  filteredObjects: DataObject[];
  updatedFilteredObjects: DataObject[];
  sortField: string = 'title';
  sortDirection: SortDirection = 'asc';

  // * Toolbar state (declarative replacements for the old actions-service config)
  selectAllChecked = false;
  counterText = '';
  /** True when the server had more rows than `resultsCap`; the list shows the first `resultsCap`. */
  resultsCapped = false;
  readonly resultsCap = DATAOBJECT_LIST_RESULTS_CAP;
  counterDisplayState: 'default' | 'zero' | 'in-range' | 'over-limit' = 'zero';

  // * Lazy-mode bookkeeping: whether the tab has ever been activated,
  // * and whether search/sort changed while inactive (so we refetch on activation).
  private hasActivated = false;
  private pendingRefetch = false;

  constructor(
    protected store: Store<AppState>,
    protected changeDetectorRef: ChangeDetectorRef,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.logger.debug('DataObjectListComponent initialized');
    this.logger.debug('DataObjectListComponent: workspace', this.workspace);
    this.logger.debug(
      'DataObjectListComponent: selectedObjectIds',
      this.selectedObjectIds,
    );
    this.logger.debug(
      'DataObjectListComponent: objectClassIds',
      this.objectClassIds,
    );
    this.logger.debug(
      'DataObjectListComponent: objectTypeIds',
      this.objectTypeIds,
    );
    this.logger.debug(
      'DataObjectListComponent: singleSelectionActive',
      this.singleSelectionActive,
    );
    this.logger.debug(
      'DataObjectListComponent: showSelectedOnly',
      this.showSelectedOnly,
    );

    this.showSelectedOnly$ = new BehaviorSubject<boolean>(
      this.showSelectedOnly,
    );

    // * Defensive check for searchString$ - parent might pass null/undefined
    if (!this.searchString$) {
      this.searchString$ = new BehaviorSubject<string>('');
    }

    // * Initialize counter state + its display
    this.initializeCounterState();
    this.refreshCounter();

    // Your filtered objects observable
    this.showSelectedOnly$
      .pipe(
        tap((showSelectedOnly) => {
          this.updatedFilteredObjects = showSelectedOnly
            ? this.filteredObjects?.filter((obj) =>
                this.selectedObjectIds.has(obj.id),
              )
            : this.filteredObjects;
        }),
      )
      .subscribe();

    const sortField$ = this.store.select(
      (state) => state.core.filteredObjectsSortingField,
    );

    const sortDirection$ = this.store.select(
      (state) => state.core.filteredObjectsSortingDirection,
    );

    const isLazy = !!this.activeTabId$ && !!this.tabId;

    combineLatest([sortField$, sortDirection$])
      .pipe(
        // Skip the initial emission (initial sort state)
        skip(1),
        // Only react when either actually changes
        distinctUntilChanged(
          ([prevF, prevD], [currF, currD]) =>
            prevF === currF && prevD === currD,
        ),

        // Update local fields
        tap(([sortingField, sortingDirection]) => {
          this.sortField = sortingField;
          this.sortDirection = sortingDirection;
        }),

        // Trigger the fetch on change — but only if this tab is already
        // active. In lazy mode, inactive tabs defer the fetch until activation.
        switchMap(() => {
          if (isLazy && !this.hasActivated) {
            this.pendingRefetch = true;
            return [];
          }
          return this.runFetch();
        }),

        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    if (isLazy) {
      // * Defer the initial fetch until the owning tab becomes active.
      // * Refetch on re-activation only when search/sort changed while inactive.
      this.activeTabId$
        .pipe(
          filter((id) => id === this.tabId),
          switchMap(() => {
            if (!this.hasActivated || this.pendingRefetch) {
              this.hasActivated = true;
              this.pendingRefetch = false;
              return this.runFetch();
            }
            return [];
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      this.runFetch()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.cdr.markForCheck()),
        )
        .subscribe();
    }

    this.searchString$
      .pipe(
        skip(1), // Skip the initial value
        tap((val) => {
          this.searchString = val;
          this.logger.debug(
            'DataObjectListComponent: searchString changed',
            this.searchString,
          );
        }),
        switchMap(() => {
          if (isLazy && !this.hasActivated) {
            this.pendingRefetch = true;
            return [];
          }
          return this.runFetch();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private runFetch(): Observable<DataObject[]> {
    this.objectsLoading$.next(true);

    return this.userObjects
      .search$({
        query: this.searchString,
        workspaceId: this.workspace ? this.workspace.id : null,
        objectClassIds: this.objectClassIds,
        objectTypeIds: this.objectTypeIds,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        // * one extra row tells us the cap was hit without a second count request
        itemsCount: DATAOBJECT_LIST_RESULTS_CAP + 1,
        lean: true,
      })
      .pipe(
        finalize(() => this.objectsLoading$.next(false)),
        tap((objects) => {
          this.resultsCapped =
            (objects?.length ?? 0) > DATAOBJECT_LIST_RESULTS_CAP;
          const filteredObjects = this.resultsCapped
            ? objects.slice(0, DATAOBJECT_LIST_RESULTS_CAP)
            : objects;
          this.filteredObjects = filteredObjects;
          this.updatedFilteredObjects = this.showSelectedOnly
            ? filteredObjects?.filter((obj) =>
                this.selectedObjectIds.has(obj.id),
              )
            : filteredObjects;
          this.cdr.markForCheck();
        }),
      );
  }

  // * Initialize counter state based on component inputs
  private initializeCounterState(): void {
    this.counterState = {
      currentSelectedCount: this.selectedObjectIds.size,
      maxSelectedCount: this.maxSelectedCount,
      objectClassIds: this.objectClassIds,
      objectTypeIds: this.objectTypeIds || [],
    };
  }

  // * Update counter state and notify service
  private updateCounterState(): void {
    this.counterState.currentSelectedCount = this.selectedObjectIds.size;
    this.refreshCounter();
  }

  // * Recompute counter text + state; warn once when the selection exceeds the max.
  private refreshCounter(): void {
    this.counterText = this.getCounterText(this.counterState);
    this.counterDisplayState = this.getCounterState(this.counterState);

    if (
      this.counterState.maxSelectedCount > 0 &&
      this.counterState.currentSelectedCount >
        this.counterState.maxSelectedCount
    ) {
      this.toastService.showWarning(
        this.translateService.instant(
          'object-picker-actions.toast.selection-limit-exceeded',
        ),
      );
    }
  }

  // * Dynamic counter text from selected object types (English, as before).
  private getCounterText(counterState: CounterState): string {
    const objectTypeLabels = this.generateObjectTypeLabels(
      this.getObjectTypesMap(counterState),
    );

    if (counterState.maxSelectedCount > 0) {
      return `Selected ${counterState.currentSelectedCount} ${objectTypeLabels} out of maximum ${counterState.maxSelectedCount}`;
    }
    return `Selected ${counterState.currentSelectedCount} ${objectTypeLabels}`;
  }

  private getObjectTypesMap(counterState: CounterState): Map<string, boolean> {
    const typesMap = new Map<string, boolean>();

    counterState.objectClassIds.forEach((classId) => {
      switch (classId) {
        case NomObjectClass.FEED:
          if (
            counterState.objectTypeIds.includes(NomObjectTypes.SEARCH_DIMENSION)
          ) {
            typesMap.set('dimension', true);
          } else {
            typesMap.set('feed', true);
          }
          break;
        case NomObjectClass.DASHBOARD:
          typesMap.set('dashboard', true);
          break;
        case NomObjectClass.USER:
          typesMap.set('user', true);
          break;
        case NomObjectClass.USER_GROUP:
          typesMap.set('group', true);
          break;
        default:
          typesMap.set('item', true);
      }
    });

    return typesMap;
  }

  private generateObjectTypeLabels(
    objectTypesMap: Map<string, boolean>,
  ): string {
    const labels: string[] = [];

    objectTypesMap.forEach((_, type) => {
      switch (type) {
        case 'feed':
          labels.push('Feed(s)');
          break;
        case 'dimension':
          labels.push('Dimension(s)');
          break;
        case 'dashboard':
          labels.push('Dashboard(s)');
          break;
        case 'user':
          labels.push('User(s)');
          break;
        case 'group':
          labels.push('Group(s)');
          break;
        default:
          labels.push('Item(s)');
      }
    });

    if (labels.length > 1) {
      const last = labels.pop();
      return `${labels.join(', ')} and ${last}`;
    }

    return labels[0] || 'Item(s)';
  }

  private getCounterState(
    counterState: CounterState,
  ): 'default' | 'zero' | 'in-range' | 'over-limit' {
    if (counterState.currentSelectedCount === 0) {
      return 'zero';
    }
    if (
      counterState.maxSelectedCount > 0 &&
      counterState.currentSelectedCount > counterState.maxSelectedCount
    ) {
      return 'over-limit';
    }
    if (
      counterState.maxSelectedCount > 0 &&
      counterState.currentSelectedCount <= counterState.maxSelectedCount
    ) {
      return 'in-range';
    }
    return 'default';
  }

  addSelectedDataObjectsToFormGroup() {
    if (this.formGroupPropertyName) {
      this.selectedObjectIds.forEach((dataObjectDef) => {
        if (dataObjectDef) {
          this.formGroup.addControl(
            `${this.formGroupPropertyName}`,
            new FormControl(dataObjectDef.object_id),
          );
        }
      });
    }
  }

  syncFormGroup() {
    this.formGroup = new FormGroup({});
    this.addSelectedDataObjectsToFormGroup();
    this.formGroupChange.emit(this.formGroup);
  }

  handleInputDataObjectSearchChange(searchString: string) {
    this.searchString$.next(searchString);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // * Update counter state when selectedObjectIds changes
    if (
      changes['selectedObjectIds'] &&
      !changes['selectedObjectIds'].firstChange
    ) {
      this.updateCounterState();
    }

    // * Update counter state when maxSelectedCount changes
    if (
      changes['maxSelectedCount'] &&
      !changes['maxSelectedCount'].firstChange
    ) {
      this.counterState.maxSelectedCount = this.maxSelectedCount;
      this.updateCounterState();
    }

    // * Update counter state when objectClassIds changes
    if (changes['objectClassIds'] && !changes['objectClassIds'].firstChange) {
      this.counterState.objectClassIds = this.objectClassIds;
      this.updateCounterState();
    }

    // * Update counter state when objectTypeIds changes
    if (changes['objectTypeIds'] && !changes['objectTypeIds'].firstChange) {
      this.counterState.objectTypeIds = this.objectTypeIds || [];
      this.updateCounterState();
    }
  }

  // * Toggles the selection of a data object.
  // * If the object is already selected, it will be deselected.
  // * If the object is not selected, it will be selected.
  toggleSelection(object: DataObject) {
    if (this.singleSelectionActive) {
      if (this.selectedObjectIds.has(object.id)) {
        this.selectedObjectIds.delete(object.id);
      } else {
        this.selectedObjectIds.clear();

        this.selectedObjectIds.set(object.id, {
          object_id: object.id,
          object_class_id: object.object_class_id,
        });
      }
    } else {
      if (this.selectedObjectIds.has(object.id)) {
        this.selectedObjectIds.delete(object.id);
      } else {
        this.selectedObjectIds.set(object.id, {
          object_id: object.id,
          object_class_id: object.object_class_id,
        });
      }
    }

    this.syncFormGroup();

    // * Update counter state
    this.updateCounterState();

    this.componentDataChange.emit(this.selectedObjectIds);
  }

  getEmptyMessage(objectClassIds: number[]): string {
    const labelMap: { [key: number]: string } = {
      [NomObjectClass.FEED]: 'feeds',
      //[NomObjectClass.DIMENSION]: 'dimensions',
      [NomObjectClass.DASHBOARD]: 'dashboards',
    };

    const labels = objectClassIds.map((id) => labelMap[id]).filter(Boolean);

    if (labels.length === 0) return 'No items found.';

    if (labels.length === 1) {
      return `There are no ${labels[0]} found.`;
    }

    const last = labels.pop();
    return `There are no ${labels.join(', ')} and ${last} found.`;
  }

  // * Checks if the given DataObject is selected.
  isSelected(object: DataObject): boolean {
    return this.selectedObjectIds.has(object.id);
  }

  // * Toggles the selected state of the objects based on the provided isSelected flag.
  toggleAllSelected(isSelected: boolean) {
    this.filteredObjects.forEach((obj) => {
      if (!isSelected) {
        this.selectedObjectIds.delete(obj.id);
      } else {
        this.selectedObjectIds.set(obj.id, {
          object_id: obj.id,
          object_class_id: obj.object_class_id,
        });
      }
    });

    // * Update counter state
    this.updateCounterState();

    // * emit componentDataChange to notify the Picker Popup
    this.componentDataChange.emit(this.selectedObjectIds);
  }

  onSelectAllChange(selected: boolean) {
    this.selectAllChecked = selected;
    this.toggleAllSelected(selected);
  }

  onSortFieldChange(sortingField: string) {
    this.sortField = sortingField;
    this.store.dispatch(setCoreFilteredObjectsSortingField({ sortingField }));
  }

  onSortDirectionChange(sortingDirection: SortDirection) {
    this.sortDirection = sortingDirection;
    this.store.dispatch(
      setCoreFilteredObjectsSortingDirection({ sortingDirection }),
    );
  }

  /** Trailing indicator next to the Sort trigger: icon of the selected sort field. */
  get sortFieldIcon(): IconProp {
    return this.sortField === 'time_created' ||
      this.sortField === 'time_modified'
      ? ['fas', 'clock']
      : ['fas', 'text-height'];
  }

  get sortLabel(): string {
    return this.translateService.instant('dataobject-list-actions.button.sort');
  }

  displaySelectedOnly(selectedOnly: boolean) {
    this.showSelectedOnly = selectedOnly;
    this.changeDetectorRef.detectChanges();
    this.showSelectedOnly$.next(this.showSelectedOnly);
  }

  trackByFn(index, item) {
    return item.id;
  }
}
